import { initializeApp } from "firebase/app";
import { initializeAuth, browserLocalPersistence, browserPopupRedirectResolver } from "firebase/auth";
import { getDatabase, ref, set, query, orderByChild, startAt, endAt, get as dbGet } from "firebase/database";
import { getStorage } from "firebase/storage";
import { geohashForPoint, geohashQueryBounds, distanceBetween } from "geofire-common";
import i18next from './i18n';

const config = {
    apiKey: "AIzaSyBRmqDg58qd5Xa3BlK4__eKNqABs5mYYS4",
    authDomain: "pinbike-1521450884928.firebaseapp.com",
    databaseURL: "https://pinbike-1521450884928.firebaseio.com",
    projectId: "pinbike-1521450884928",
    storageBucket: "pinbike-1521450884928.appspot.com",
    messagingSenderId: "1080799050187",
    appId: "1:1080799050187:web:e13b7347cb977648aa5e4e"
};

const testConfig = {
    apiKey: "AIzaSyA0LsHLFJxNm2Rkw8FHC07gYNQmeOtwzIM",
    authDomain: "tester-nxt.firebaseapp.com",
    databaseURL: "https://tester-nxt-default-rtdb.europe-west1.firebasedatabase.app",
    projectId: "tester-nxt",
    storageBucket: "tester-nxt.appspot.com",
    messagingSenderId: "8972695822",
    appId: "1:8972695822:web:541525630a8bb3e1203f5f"
};

const collConfig = {
    apiKey: "AIzaSyDF7CxRpxuLPSjsdy0cDhchb1aLtIFlKf8",
    authDomain: "pinbike-coll.firebaseapp.com",
    databaseURL: "https://pinbike-coll-default-rtdb.europe-west1.firebasedatabase.app",
    projectId: "pinbike-coll",
    storageBucket: "pinbike-coll.appspot.com",
    messagingSenderId: "345237933284",
    appId: "1:345237933284:web:a7fc97c0817ef6c7e9b0f1",
    measurementId: "G-98L0F7D99M"
};

const activeConfig =
    process.env.REACT_APP_ENVIRONMENT === "production" ? config :
    process.env.REACT_APP_ENVIRONMENT === "coll" ? collConfig :
    testConfig;

const app = initializeApp(activeConfig);

export const firebaseAuth = initializeAuth(app, {
    persistence: browserLocalPersistence,
    popupRedirectResolver: browserPopupRedirectResolver,
});
export const firebaseDatabase = getDatabase(app);
export const firebaseStorage = getStorage(app);

// ─── Sostituti per GeoFire ────────────────────────────────────────────────────
// geofire-common fornisce solo funzioni matematiche; le query/write le facciamo
// direttamente con Firebase Realtime Database.

/**
 * Salva una posizione geohashata su un path del database.
 * Equivalente a geoFire.set(key, [lat, lng])
 */
export async function geoSet(basePath, key, lat, lng) {
    const hash = geohashForPoint([lat, lng]);
    const nodeRef = ref(firebaseDatabase, `${basePath}/${key}`);
    await set(nodeRef, { g: hash, l: [lat, lng] });
}

/**
 * Recupera tutti i punti entro un raggio da un centro.
 * Equivalente a geoFire.query({ center, radius }).on('key_entered')
 *
 * @param {string} basePath      - es. '/drinkingFountainsGeoHashed'
 * @param {[number,number]} center - [lat, lng]
 * @param {number} radiusKm      - raggio in km
 * @returns {Promise<Array>}     - array di { key, lat, lng, distance }
 */
export async function geoQuery(basePath, center, radiusKm) {
    const bounds = geohashQueryBounds(center, radiusKm * 1000);
    const baseRef = ref(firebaseDatabase, basePath);
    const results = [];

    const snapshots = await Promise.all(
        bounds.map(([start, end]) =>
            dbGet(query(baseRef, orderByChild('g'), startAt(start), endAt(end)))
        )
    );

    snapshots.forEach(snap => {
        snap.forEach(child => {
            const data = child.val();
            const [lat, lng] = data.l;
            const dist = distanceBetween([lat, lng], center);
            if (dist <= radiusKm) {
                results.push({ key: child.key, lat, lng, distance: dist, ...data });
            }
        });
    });

    return results;
}

// Path constants — usali al posto dei vecchi oggetti geoFire
export const GEO_PATHS = {
    drinkingFountains: '/drinkingFountainsGeoHashed',
    publicUtility: '/publicUtilityGeoHashed',
    report: '/reportGeoHashed',
};

export function getErrorMessage(e) {
    switch (e.code) {
        case "auth/email-already-in-use":
            return i18next.t('emailAlreadyInUse');
        case "auth/invalid-email":
            return i18next.t('emailInvalid');
        case "auth/weak-password":
            return i18next.t('weakPassword');
        case "auth/user-not-found":
            return i18next.t('userNotFound');
        case "auth/wrong-password":
            return i18next.t('wrongPassword');
        case "auth/network-request-failed":
            return i18next.t('networkRequestFailed');
        case "auth/popup-blocked":
            return i18next.t('popupBlocked');
        case "auth/unauthorized-domain":
            return i18next.t('unauthorizedDomain');
        case "auth/operation-not-allowed":
            return i18next.t('operationNotAllowed');
        case "auth/popup-closed-by-user":
        case "auth/cancelled-popup-request":
            return '';
        default:
            return e.message || i18next.t('internalServerError');
    }
}